import React, { PureComponent } from 'react';
import * as dates from '@simplybenefits/sb-dates';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { MessageCard, InvoiceTile, Button, CurrentTransaction, Container, Content, Title, Theme, i18n, tokens } from '@simplybenefits/sb-ui-library';
import { setCurrentSidebarItem } from '../../actions/user';
import { getInvoice } from '../../actions/invoices';
import { ExportToCsv } from 'export-to-csv-fix-source-map';
import { downloadPDFToBrowser } from '../../services/helpers';

const benefits = {
    'life': {
        title: 'Life',
        volumeType: 'number'
    },
    'add': {
        title: 'AD&D',
        volumeType: 'number'
    },
    'ltd': {
        title: 'LTD',
        volumeType: 'number'
    },
    'std': {
        title: 'STD',
        volumeType: 'number'
    },
    'depLife': {
        title: 'Dep Life',
        volumeType: 'text'
    },
    'ci': {
        title: 'CI',
        volumeType: 'text'
    },
    'ciSpouse': {
        title: 'CI (Spouse)',
        volumeType: 'text'
    },
    'ciChild': {
        title: 'CI (Child)',
        volumeType: 'text'
    },
    'health': {
        title: 'Health',
        volumeType: 'text'
    },
    'outOfCountry': {
        title: 'Out Of Country',
        volumeType: 'text'
    },
    'stopLoss': {
        title: 'Stop Loss',
        volumeType: 'text'
    },
    'hsa': {
        title: 'HSA',
        volumeType: 'text'
    },
    'lsa': {
        title: 'LSA',
        volumeType: 'text'
    },
    'costPlus': {
        title: 'Cost Plus',
        volumeType: 'text'
    },
    'wello': {
        title: 'Wello',
        volumeType: 'text'
    },
    "virtualHealthcare": {
        title: 'Virtual Healthcare',
        volumeType: 'text'
    },
    'efap': {
        title: 'EFAP',
        volumeType: 'text'
    },
    'dental': {
        title: 'Dental',
        volumeType: 'text'
    },
    'marketplace': {
        title: 'Marketplace',
        volumeType: 'dash'
    },
    'miscPremiums': {
        title: 'Misc',
        volumeType: 'dash'
    },
    'totals': {
        title: 'Totals',
        volumeType: 'number'
    }
};

class Invoices extends PureComponent {
    state = {
        invoice: null
    };

    componentDidMount() {
        this.props.dispatch(setCurrentSidebarItem('invoices'));
    }

    _selectInvoice = async (selectedInvoice) => {
        const invoice = await this.props.dispatch(getInvoice(selectedInvoice.invoiceNum));

        this.setState({
            invoice: {
                ...invoice,
                meta: {
                    ...selectedInvoice,
                    ...invoice.meta
                }
            }
        });
    };

    _showHistory = () => {
        this.setState({
            invoice: null
        });
    };

    /**
     *
     * @param {*} number
     */
    formatNumber = (number) => `$${ (number || 0).toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') }`;

    /**
     *
     * @param {*} benefit
     * @param {*} transaction
     * @returns
     */
    _determineWhetherToShow = (benefit) => {
        if (benefit === "wello" && typeof this.state.invoice.amounts[benefit]?.premium !== "number") {
            return null;
        }

        return (
            (benefit !== "totals") &&
        (this.state.invoice.amounts[benefit]?.premium > 0 || (this.state.invoice.amounts[benefit]?.adjustments instanceof Object ?
            this.state.invoice.amounts[benefit]?.adjustments.total
            :
            this.state.invoice.amounts[benefit]?.adjustments !== 0))
        );
    };

    /**
     *
     * @param {*} x
     */
    _numberWithCommas = (x) => x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    _downloadCSV = (invoiceNum) => {
        let items = [];

        // eslint-disable-next-line no-unused-vars
        Object.keys(benefits).map((benefit, i) => {
            const benefitParams = benefits[benefit];
            let volume = '-';
            let rate = 0;
            let premium = 0;
            let adj = 0;
            let HST = 0;
            let GST = 0;
            let PST = 0;
            let RST = 0;

            if (this.state.invoice.amounts[benefit]) {
                // this first block is to support very old legacy invoices
                if (((benefit === 'marketplace') || benefit === 'miscPremiums') && this.state.invoice.amounts[benefit].totals) {
                    premium = this.state.invoice.amounts[benefit].totals.premium;
                    HST = this.state.invoice.amounts[benefit].totals.HST;
                    GST = this.state.invoice.amounts[benefit].totals.GST;
                    PST = this.state.invoice.amounts[benefit].totals.PST;
                    RST = this.state.invoice.amounts[benefit].totals.RST || 0;
                    adj = this.state.invoice.amounts[benefit].totals.adjustments;
                    rate = this.state.invoice.amounts[benefit].rate;
                } else {
                    premium = this.state.invoice.amounts[benefit].premium;
                    HST = this.state.invoice.amounts[benefit].HST;
                    GST = this.state.invoice.amounts[benefit].GST;
                    PST = this.state.invoice.amounts[benefit].PST;
                    RST = this.state.invoice.amounts[benefit].RST || 0;
                    rate = this.state.invoice.amounts[benefit].rate;

                    if (this.state.invoice.amounts[benefit].adjustments instanceof Object) {
                        adj = this.state.invoice.amounts[benefit].adjustments.net;
                        // need to add the adjustment taxes to the overall benefit taxes
                        HST += this.state.invoice.amounts[benefit].adjustments.HST;
                        GST += this.state.invoice.amounts[benefit].adjustments.GST;
                        PST += this.state.invoice.amounts[benefit].adjustments.PST;
                        RST += this.state.invoice.amounts[benefit].adjustments.RST || 0;
                    } else {
                        adj = this.state.invoice.amounts[benefit].adjustments;
                    }

                    if (benefitParams.volumeType === 'number') {
                        volume = this._numberWithCommas(Math.ceil(this.state.invoice.amounts[benefit].volume));
                    } else if (benefitParams.volumeType === 'text') {
                        volume = this.state.invoice.amounts[benefit].coverageType;
                    }
                }
            }

            if (this._determineWhetherToShow(benefit)) {
                items.push({ 
                    "benefit": benefitParams.title === `Wello` ? "Maple" : benefitParams.title,
                    "volume": volume,
                    "rate": this.formatNumber(rate),
                    "premium": this.formatNumber(premium),
                    "adj": this.formatNumber(adj),
                    "gst/hst": this.formatNumber(GST + HST),
                    "pst": this.formatNumber(PST + RST),
                    "totals": this.formatNumber(premium + GST + HST + PST + RST + adj)
                });
            }
        });

        items.push({
            "benefit": "Carry Forward",
            "volume": "-",
            "rate": "-",
            "premium": "-",
            "adj": "-",
            "gst/hst": "-",
            "pst": "-",
            "totals": this.formatNumber(this.state.invoice.amounts.totals.carryForward)
        });

        items.push({
            "benefit": "Totals",
            "volume": "-",
            "rate": "-",
            "premium": this.formatNumber(this.state.invoice.amounts.totals.premium),
            "adj": this.state.invoice.amounts.totals.adjustments instanceof Object ?
                this.formatNumber(this.state.invoice.amounts.totals.adjustments.net) :
                this.formatNumber(this.state.invoice.amounts.totals.adjustments),
            "gst/hst": this.formatNumber(this.state.invoice.amounts.totals.HST + this.state.invoice.amounts.totals.GST),
            "pst": this.formatNumber(this.state.invoice.amounts.totals.PST + (this.state.invoice.amounts.totals.RST || 0)),
            "totals": this.formatNumber(this.state.invoice.amounts.totals.grandTotal + this.state.invoice.amounts.totals.carryForward)
        });

        let title = `Invoice-${ invoiceNum }`;

        if (this.props.dateRange) {
            title += `(${ this.props.dateRange.startDate } - ${ this.props.dateRange.endDate })`;
        }

        const options = {
            title,
            fieldSeparator: ',',
            filename: `${ invoiceNum }-${ new Date().toLocaleDateString() }`,
            quoteStrings: '"',
            decimalSeparator: '.',
            showLabels: true,
            showTitle: true,
            useTextFile: false,
            useBom: true,
            headers: [ "Benefit/Service", "Volume", "Rate", "Premium", "Adj.", "GST/HST", "PST", "Totals" ]
        };

        const csvExporter = new ExportToCsv(options);
        csvExporter.generateCsv(items);
    };

    
    render() {

        const styles = {
            contentBlock: { 
                backgroundColor: Theme.colors.surface,
                boxShadow: '0 1px 2px rgba(0, 0, 0, .15)',
                display: 'block',
                margin: 'auto auto 30px auto',
                padding: '30px',
                position: 'relative',
                zIndex: 3
            },
            invoiceHistoryContainer: {
                display: 'flex',
                flexWrap: 'wrap',
                flexDirection: 'row'
            },
            titleBlock: {
                display: 'flex',
                flex: 1,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: "space-between"
            }
        };

        if (this.state.invoice) {
            return (
                <Container>
                    <Content>
                        <div style={ styles.titleBlock }>
                            <Title title={ i18n.t(tokens.titles.invoiceDate, { date: dates.getLocalDisplay(this.state.invoice.meta.dateAdded, 'MMMM YYYY', i18n.locale) }) } customStyle={{ flex: 1 }} />

                            <Button
                                id="view-history"
                                color="primary"
                                customStyle={{
                                    height: 50,
                                    width: 180,
                                    textTransform: "capitalize",
                                    fontSize: 16
                                }}
                                onClick={ this._showHistory }
                            >
                                { i18n.t(tokens.labels.invoices.viewHistory) }
                            </Button>
                        </div>

                        <div style={ styles.contentBlock }>

                            <Button
                                id="view-history"
                                transparent
                                customStyle={{
                                    height: 50,
                                    textTransform: "capitalize",
                                    fontSize: 16,
                                    paddingLeft: "10px",
                                    paddingRight: "10px",
                                    mrginRight: "10px"
                                }}
                                onClick={ () => downloadPDFToBrowser("MemberInvoice", `Invoice-${ this.state.invoice.invoiceNum }`, this.props.dispatch, { invoiceNum: this.state.invoice.invoiceNum }) }
                            >
                                { i18n.t(tokens.buttons["download invoice pdf"]) }
                            </Button>

                            <Button
                                id="view-history"
                                transparent
                                customStyle={{
                                    height: 50,
                                    textTransform: "capitalize",
                                    fontSize: 16,
                                    paddingLeft: "10px",
                                    paddingRight: "10px"
                                }}
                                onClick={ () => this._downloadCSV(this.state.invoice.invoiceNum) }
                            >
                                { i18n.t(tokens.buttons["download invoice csv"]) }
                            </Button>

                            <CurrentTransaction transaction={ this.state.invoice } isMemberInvoice={ true } />
                        </div>
                    </Content>
                </Container>
            );
        }

        return (
            <Container>
                <Content>
                    <Title title={ i18n.t(tokens.titles["invoice history"])} customStyle={{ flex: 1 }} />

                    {
                        this.props.invoices.length === 0 ?
                            <MessageCard
                                title={ i18n.t(tokens.titles["no invoice history"]) }
                                subtitle={ i18n.t(tokens.labels.invoices.youHaveNotBeenCharged) }
                            />
                            :
                            <div style={ styles.invoiceHistoryContainer }>
                                {
                                    this.props.invoices.map((invoice, i) => (
                                        <InvoiceTile
                                            testid={i === 0 ? 'invoice-history-tile' : undefined}
                                            key={invoice.dateAdded}
                                            date={invoice.dateAdded}
                                            total={ invoice.grandTotal }
                                            status={ invoice.status }
                                            isFirstInvoice={ i === (this.props.invoices.length - 1) }
                                            onClick={() => this._selectInvoice(invoice)}
                                        />
                                    ))
                                }
                            </div>
                    }
                </Content>

                <span id="claims" />
            </Container>
        );
    }
}

Invoices.propTypes = {
    loading: PropTypes.bool,
    invoices: PropTypes.array
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loading: state.invoices.loading,
    invoices: state.invoices.items
});

export default connect(mapStateToProps)(Invoices);